/**
 * LeaderboardTable Component
 *
 * Rich leaderboard table with domain, badges, for-hire indicators, and current user highlighting.
 */

import { Link } from 'react-router-dom';
import clsx from 'clsx';

export interface LeaderboardEntry {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  avatarInitials?: string;
  authorityScore: number;
  domainKey?: string | null;
  domainName?: string | null;
  rank: number;
  badges?: string[];
  isHireable?: boolean;
  publicSlug?: string | null;
  sessionsCount?: number;
  location?: string;
}

export interface LeaderboardTableProps {
  entries: LeaderboardEntry[];
  currentUserId?: string | null;
  showDomain?: boolean;
  showBadges?: boolean;
  showHireStatus?: boolean;
  emptyMessage?: string;
  isLoading?: boolean;
}

export function LeaderboardTable({
  entries,
  currentUserId,
  showDomain = true,
  showBadges = true,
  showHireStatus = true,
  emptyMessage = 'No entries yet. Be the first to climb the ranks!',
  isLoading = false,
}: LeaderboardTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="animate-pulse flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-8 h-8 bg-gray-200 rounded-full" />
            <div className="w-10 h-10 bg-gray-200 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/3" />
              <div className="h-3 bg-gray-200 rounded w-1/4" />
            </div>
            <div className="h-8 bg-gray-200 rounded w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <TrophyIcon className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      {/* Table header */}
      <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
        <div className="col-span-1 text-center">Rank</div>
        <div className="col-span-4">User</div>
        {showDomain && <div className="col-span-2 hidden md:block">Domain</div>}
        {showBadges && <div className="col-span-2 hidden lg:block">Badges</div>}
        <div className={clsx('text-right', showDomain && showBadges ? 'col-span-3' : 'col-span-5')}>
          Authority Score
        </div>
      </div>

      {/* Table body */}
      <div className="divide-y divide-gray-100">
        {entries.map((entry) => (
          <LeaderboardRow
            key={entry.userId}
            entry={entry}
            isCurrentUser={entry.userId === currentUserId}
            showDomain={showDomain}
            showBadges={showBadges}
            showHireStatus={showHireStatus}
          />
        ))}
      </div>
    </div>
  );
}

interface LeaderboardRowProps {
  entry: LeaderboardEntry;
  isCurrentUser: boolean;
  showDomain: boolean;
  showBadges: boolean;
  showHireStatus: boolean;
}

function LeaderboardRow({
  entry,
  isCurrentUser,
  showDomain,
  showBadges,
  showHireStatus,
}: LeaderboardRowProps) {
  const isTopThree = entry.rank <= 3;
  const initials = entry.avatarInitials || getInitials(entry.displayName);

  const userContent = (
    <div className="flex items-center gap-3">
      {/* Avatar */}
      {entry.avatarUrl ? (
        <img
          src={entry.avatarUrl}
          alt={entry.displayName}
          className={clsx(
            'w-10 h-10 rounded-full object-cover',
            isCurrentUser && 'ring-2 ring-primary-500'
          )}
        />
      ) : (
        <div
          className={clsx(
            'w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm',
            isCurrentUser
              ? 'bg-primary-500 text-white'
              : 'bg-gradient-to-br from-gray-200 to-gray-300 text-gray-700'
          )}
        >
          {initials}
        </div>
      )}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={clsx(
              'font-medium truncate',
              isCurrentUser ? 'text-primary-700' : 'text-gray-900'
            )}
          >
            {entry.displayName}
          </span>
          {isCurrentUser && (
            <span className="px-1.5 py-0.5 bg-primary-100 text-primary-700 text-xs rounded font-medium">
              You
            </span>
          )}
          {showHireStatus && entry.isHireable && (
            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium">
              For Hire
            </span>
          )}
        </div>
        {entry.location && (
          <span className="text-xs text-gray-500">{entry.location}</span>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={clsx(
        'grid grid-cols-12 gap-2 px-4 py-4 items-center transition-colors',
        isCurrentUser ? 'bg-primary-50' : 'hover:bg-gray-50'
      )}
    >
      {/* Rank */}
      <div className="col-span-1 text-center">
        {isTopThree ? (
          <span
            className={clsx(
              'inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm',
              entry.rank === 1 && 'bg-yellow-100 text-yellow-700',
              entry.rank === 2 && 'bg-gray-200 text-gray-700',
              entry.rank === 3 && 'bg-orange-100 text-orange-700'
            )}
          >
            {entry.rank === 1 ? <TrophyIcon className="w-4 h-4" /> : entry.rank}
          </span>
        ) : (
          <span className="text-sm font-medium text-gray-500">#{entry.rank}</span>
        )}
      </div>

      {/* User info - linkable if public slug exists */}
      <div className="col-span-4">
        {entry.publicSlug ? (
          <Link
            to={`/u/${entry.publicSlug}`}
            className="hover:opacity-80 transition-opacity"
          >
            {userContent}
          </Link>
        ) : (
          userContent
        )}
      </div>

      {/* Domain */}
      {showDomain && (
        <div className="col-span-2 hidden md:block">
          {entry.domainName || entry.domainKey ? (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full truncate">
              {entry.domainName || formatDomainKey(entry.domainKey)}
            </span>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </div>
      )}

      {/* Badges */}
      {showBadges && (
        <div className="col-span-2 hidden lg:flex flex-wrap gap-1">
          {entry.badges && entry.badges.length > 0 ? (
            entry.badges.slice(0, 3).map((badge, idx) => (
              <span
                key={idx}
                className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded"
                title={badge}
              >
                {formatBadgeName(badge)}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
          {entry.badges && entry.badges.length > 3 && (
            <span className="text-xs text-gray-400">+{entry.badges.length - 3}</span>
          )}
        </div>
      )}

      {/* Authority Score */}
      <div className={clsx('text-right', showDomain && showBadges ? 'col-span-3' : 'col-span-5')}>
        <span
          className={clsx(
            'inline-flex items-center px-3 py-1 rounded-full text-sm font-bold',
            entry.authorityScore >= 90
              ? 'bg-green-100 text-green-700'
              : entry.authorityScore >= 80
              ? 'bg-primary-100 text-primary-700'
              : entry.authorityScore >= 70
              ? 'bg-yellow-100 text-yellow-700'
              : 'bg-gray-100 text-gray-700'
          )}
        >
          {entry.authorityScore}
        </span>
      </div>
    </div>
  );
}

// Helper functions
function getInitials(name: string): string {
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function formatDomainKey(domainKey?: string | null): string {
  if (!domainKey) return '';
  return domainKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatBadgeName(badge: string): string {
  // Shorten badge names for display
  const shortNames: Record<string, string> = {
    domain_mastery: 'Master',
    assessment_streak: 'Streak',
    top_performer: 'Top',
    early_adopter: 'Pioneer',
  };
  return shortNames[badge] || badge.substring(0, 6);
}

// Icon components
function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 1l3 3.308 4.484.653-3.244 3.162.766 4.466L10 10.2l-4.006 2.389.766-4.466L3.516 4.96 8 4.308 10 1z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// Export a compact version for widgets
export function LeaderboardCompact({
  entries,
  currentUserId,
  title = 'Top Performers',
  maxItems = 5,
}: {
  entries: LeaderboardEntry[];
  currentUserId?: string | null;
  title?: string;
  maxItems?: number;
}) {
  const displayEntries = entries.slice(0, maxItems);

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-900 mb-3">{title}</h4>
      <div className="space-y-2">
        {displayEntries.map((entry) => {
          const isCurrentUser = entry.userId === currentUserId;
          const initials = entry.avatarInitials || getInitials(entry.displayName);

          return (
            <div
              key={entry.userId}
              className={clsx(
                'flex items-center gap-3 p-2 rounded-lg',
                isCurrentUser ? 'bg-primary-50' : 'hover:bg-gray-50'
              )}
            >
              <span
                className={clsx(
                  'w-6 text-center text-sm font-medium',
                  entry.rank === 1
                    ? 'text-yellow-600'
                    : entry.rank === 2
                    ? 'text-gray-500'
                    : entry.rank === 3
                    ? 'text-orange-600'
                    : 'text-gray-400'
                )}
              >
                {entry.rank}
              </span>
              <div
                className={clsx(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium',
                  isCurrentUser
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                )}
              >
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {entry.displayName}
                  </span>
                  {entry.isHireable && (
                    <BriefcaseIcon className="w-3 h-3 text-green-600" />
                  )}
                </div>
              </div>
              <span className="text-sm font-bold text-gray-700">{entry.authorityScore}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BriefcaseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}
